import { ART_CREDITS, DATASET_CREDITS, SOFTWARE_CREDITS } from './data/credits';
import { creditsHtml } from './credits';
import { frameMeterItem } from './frameMeter';

/**
 * The game menu (M63, the user's word: *"create a game menu that allows you
 * to start a new game. put a credit button in there which shows the
 * credits"*): opened from the top bar, it starts a new game (after asking,
 * since the game in progress ends) and shows the credits. The clock is held
 * while it is open and let go as it was when it closes, so nothing happens
 * behind it.
 *
 * Its rules are `menuStep`, a function of the view and the press alone, so
 * the suite holds them without a browser; `GameMenu` draws the view it gives.
 * Like the HUD's buttons, the menu's are built once and never redrawn (M9).
 */

/** Where the menu is: shut, its list, asking before a new game, or the credits. */
export type MenuView = 'closed' | 'main' | 'confirm' | 'credits';

/** What the player pressed: the top bar's Menu, one of the menu's buttons, or the Escape key. */
export type MenuPress = 'menu' | 'resume' | 'new' | 'start' | 'cancel' | 'credits' | 'guide' | 'fps' | 'back' | 'escape' | 'save' | 'load';

export interface MenuStep {
  view: MenuView;
  /** The game in progress ends and a new one begins (only from the question, answered). */
  newGame: boolean;
  /** The menu opened from the game with this press (the clock held), or closed to it (the clock given back as it was). */
  opened: boolean;
  closed: boolean;
}

/**
 * The menu's rules. Menu opens it from the game; Resume, or Escape, closes
 * it. New game asks first, and only its answer starts one; Cancel, Back and
 * Escape go back to the list. A press that means nothing where the menu is
 * leaves it there.
 */
export function menuStep(view: MenuView, press: MenuPress): MenuStep {
  const to = (next: MenuView, newGame = false): MenuStep =>
    ({ view: next, newGame, opened: view === 'closed' && next !== 'closed', closed: view !== 'closed' && next === 'closed' });
  switch (view) {
    case 'closed':
      return to(press === 'menu' ? 'main' : 'closed');
    case 'main':
      // The field guide (M160) shut the menu, opened on the sanctuary's card.
      // A load (M237: Save and Load moved here from the top bar) shut it on the game loaded; a save keeps it open.
      if (press === 'resume' || press === 'escape' || press === 'guide' || press === 'load') return to('closed');
      if (press === 'new') return to('confirm');
      if (press === 'credits') return to('credits');
      return to('main');
    case 'confirm':
      if (press === 'start') return to('closed', true);
      if (press === 'cancel' || press === 'escape') return to('main');
      return to('confirm');
    case 'credits':
      return to(press === 'back' || press === 'escape' ? 'main' : 'credits');
  }
}

export interface MenuHandlers {
  /** The menu has opened: hold the clock. */
  opened(): void;
  /** The menu has closed: let the clock go as it was. */
  closed(): void;
  /** End the game in progress and begin a new one, and tell the player. */
  newGame(): void;
  /** The field guide opened on the sanctuary's card (M160). */
  fieldGuide(): void;
  /** Whether the frame rate meter is shown (M166). */
  frameMeterOn(): boolean;
  /** The frame rate meter turned on or off (M166); whether it is now on. */
  toggleFrameMeter(): boolean;
  /** The game saved now and loaded back (M237: from the top bar to the menu), each telling the player. */
  save?(): void;
  load?(): void;
  /** Whether the developer's tools are shown (M237): the frame rate meter's item only then. */
  devMode?(): boolean;
}

/**
 * The menu on screen (above): an overlay over the whole game, its views built
 * once. While it is open the rest of the HUD is inert, so the keyboard reaches
 * nothing under it either (M63's reviewer: Tab and Enter pressed the speed
 * buttons, Save and Load behind it): focus goes to each view's safe button
 * (Resume, Cancel, Back; `data-focus`) and back to where it was when the menu
 * closes.
 */
export class GameMenu {
  private readonly el: HTMLElement;
  private readonly views: readonly HTMLElement[];
  private readonly creditsBody: HTMLElement;
  private readonly fpsItem: HTMLElement | null;
  private view: MenuView = 'closed';
  private creditsDrawn = false;
  /** What had focus when the menu opened, given it back when the menu closes. */
  private focusBefore: HTMLElement | null = null;

  constructor(parent: HTMLElement, private readonly handlers: MenuHandlers) {
    this.el = document.createElement('div');
    this.el.className = 'menu';
    this.el.hidden = true;
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.setAttribute('aria-label', 'Game menu');
    this.el.innerHTML = `
      <div class="menu-panel">
        <section data-view="main">
          <h2>Seven Seas Conservancy</h2>
          <div class="menu-list">
            <button data-press="resume" data-focus>Resume</button>
            <button data-press="guide">Field guide</button>
            <hr>
            <button data-press="save">Save game</button>
            <button data-press="load">Load saved game</button>
            <p class="note menu-autosave">The game also saves itself every 30 seconds and when the page is left, and opens where it was left.</p>
            <hr>
            <button data-press="credits">Credits</button>
            <button data-press="fps" class="menu-fps" hidden></button>
            <button data-press="new" class="menu-danger">Start a new game…</button>
          </div>
        </section>
        <section data-view="confirm" hidden>
          <h2>Start a new game?</h2>
          <p class="note">The game in progress ends here, and anything not saved is lost.</p>
          <div class="menu-list">
            <button data-press="start">Start a new game</button>
            <button data-press="cancel" data-focus>Cancel</button>
          </div>
        </section>
        <section data-view="credits" hidden>
          <div class="menu-back"><button data-press="back" data-focus>Back</button></div>
          <div class="credits"></div>
        </section>
      </div>`;
    parent.appendChild(this.el);
    this.views = [...this.el.querySelectorAll<HTMLElement>('[data-view]')];
    this.creditsBody = this.el.querySelector('.credits') as HTMLElement;
    this.fpsItem = this.el.querySelector<HTMLElement>('.menu-fps');
    this.el.addEventListener('click', (event) => {
      const press = (event.target as HTMLElement).closest<HTMLElement>('[data-press]')?.dataset.press;
      if (press) this.press(press as MenuPress);
    });
  }

  get isOpen(): boolean {
    return this.view !== 'closed';
  }

  /** A press from anywhere: the top bar's Menu, a button here, or the Escape key. */
  press(press: MenuPress): void {
    const was = this.view;
    const step = menuStep(was, press);
    // The new game started while the menu is still open, so what it gives back waits for the menu to close (review
    // M116: closed first, a new game from a watched day set its speed at once and the closing put the watch's pause back).
    if (step.newGame) this.handlers.newGame();
    if (press === 'save' && was === 'main') this.handlers.save?.();
    this.view = step.view;
    // The frame rate meter's item a developer's alone (M237).
    if (this.fpsItem) this.fpsItem.hidden = !(this.handlers.devMode?.() ?? false);
    // The frame rate meter turned on or off where the menu stays open (M166), its item saying which way it now is.
    const fps = press === 'fps' && was === 'main' ? this.handlers.toggleFrameMeter() : step.view === 'main' && was !== 'main' ? this.handlers.frameMeterOn() : null;
    if (fps !== null && this.fpsItem) this.fpsItem.textContent = frameMeterItem(fps);
    if (step.view === 'credits' && !this.creditsDrawn) {
      // Drawn the first time it is shown, then kept: the credits change only with a new build.
      this.creditsBody.innerHTML = creditsHtml(DATASET_CREDITS, SOFTWARE_CREDITS, ART_CREDITS);
      this.creditsDrawn = true;
    }
    this.el.hidden = step.view === 'closed';
    for (const section of this.views) section.hidden = section.dataset.view !== step.view;
    if (step.view !== was) this.el.querySelector('.menu-panel')?.scrollTo(0, 0);
    if (step.opened) {
      this.focusBefore = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.holdTheRest(true);
    }
    if (step.view !== was && step.view !== 'closed') this.el.querySelector<HTMLElement>(`[data-view="${step.view}"] [data-focus]`)?.focus();
    if (step.closed) {
      this.holdTheRest(false);
      this.focusBefore?.focus();
      this.focusBefore = null;
    }
    if (step.opened) this.handlers.opened();
    if (step.closed) this.handlers.closed();
    if (step.closed && press === 'guide') this.handlers.fieldGuide();
    if (step.closed && press === 'load') this.handlers.load?.();
  }

  /** The HUD's other parts inert (no focus, no press) while the menu is open, and live again when it closes. */
  private holdTheRest(held: boolean): void {
    for (const part of this.el.parentElement?.children ?? []) {
      if (part !== this.el && part instanceof HTMLElement) part.inert = held;
    }
  }
}
